import { useState, type FormEvent } from "react";
import {
  useGetList,
  useNotify,
  useRefresh,
  useTranslate,
  useUpdate,
} from "ra-core";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

import {
  OPERATION_ADMINISTRATIVE_STATUSES,
  OPERATION_SEMAPHORES,
  OPERATION_TYPES,
  type Client,
  type OperationSummary,
} from "../types";
import { SemaphoreBadge, StatusLabel } from "./OperationBadges";
import { canSeeClient, type OperationControls } from "./operationRules";
import type { AppRole } from "../types";

const SELECT_CLASS =
  "h-11 w-full rounded-md border bg-background px-3 text-sm sm:h-9";

/**
 * Almikia Epic E4 (TASK-027): administrative block. Editable (as allowed by
 * `controls`) by administracion, direccion and lead_manager; compliance edits
 * only the semaforo and its reason; every other role sees read-only values
 * and no buttons. Documents and the audit log arrive in a later phase.
 */
export const OperationAdminForm = ({
  operation,
  controls,
  role,
}: {
  operation: OperationSummary;
  controls: OperationControls;
  role: AppRole;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [update, { isPending }] = useUpdate();
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState(operation.administrative_status);
  const [semaphore, setSemaphore] = useState(operation.semaphore);
  const [reason, setReason] = useState(operation.semaphore_reason ?? "");
  const [type, setType] = useState(operation.operation_type);
  const [clientId, setClientId] = useState(
    operation.client_id != null ? String(operation.client_id) : "",
  );

  const canEdit = controls.canEditAdministrative || controls.canEditSemaphore;
  const showClient = controls.canEditAdministrative && canSeeClient(role);
  const { data: clients } = useGetList<Client>(
    "clients",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "full_legal_name", order: "ASC" },
    },
    { enabled: showClient },
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    const data: Record<string, unknown> = {
      semaphore,
      semaphore_reason: reason.trim() === "" ? null : reason.trim(),
    };
    if (controls.canEditAdministrative) {
      data.administrative_status = status;
      data.operation_type = type;
      if (showClient)
        data.client_id = clientId === "" ? null : Number(clientId);
    }
    update(
      "operations",
      { id: operation.id, data, previousData: operation },
      {
        onSuccess: () => {
          notify("crm.operations.done.admin", { type: "success" });
          refresh();
        },
        onError: (e) =>
          setError(
            e instanceof Error ? e.message : translate("crm.operations.error"),
          ),
      },
    );
  };

  if (!canEdit) {
    return (
      <dl className="grid gap-3 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-muted-foreground">
            {translate("crm.operations.fields.administrative_status")}
          </dt>
          <dd>
            <StatusLabel
              kind="administrative"
              value={operation.administrative_status}
            />
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">
            {translate("crm.operations.fields.operation_type")}
          </dt>
          <dd>
            {translate(`crm.operations.type.${operation.operation_type}`)}
          </dd>
        </div>
      </dl>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      {controls.canEditAdministrative ? (
        <>
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="admin-status">
              {translate("crm.operations.fields.administrative_status")}
            </label>
            <select
              id="admin-status"
              className={SELECT_CLASS}
              value={status}
              onChange={(e) =>
                setStatus(
                  e.target.value as typeof operation.administrative_status,
                )
              }
            >
              {OPERATION_ADMINISTRATIVE_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {translate(`crm.operations.administrative_status.${value}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="admin-type">
              {translate("crm.operations.fields.operation_type")}
            </label>
            <select
              id="admin-type"
              className={SELECT_CLASS}
              value={type}
              onChange={(e) =>
                setType(e.target.value as typeof operation.operation_type)
              }
            >
              {OPERATION_TYPES.map((value) => (
                <option key={value} value={value}>
                  {translate(`crm.operations.type.${value}`)}
                </option>
              ))}
            </select>
          </div>
          {showClient ? (
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="admin-client">
                {translate("crm.operations.fields.client")}
              </label>
              <select
                id="admin-client"
                className={SELECT_CLASS}
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
              >
                <option value="">
                  {translate("crm.operations.no_client")}
                </option>
                {(clients ?? []).map((client) => (
                  <option key={client.id} value={String(client.id)}>
                    {client.full_legal_name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </>
      ) : null}
      <div className="space-y-1">
        <label className="text-sm font-medium" htmlFor="admin-semaphore">
          {translate("crm.operations.fields.semaphore")}
        </label>
        <select
          id="admin-semaphore"
          className={SELECT_CLASS}
          value={semaphore}
          onChange={(e) =>
            setSemaphore(e.target.value as typeof operation.semaphore)
          }
        >
          {OPERATION_SEMAPHORES.map((value) => (
            <option key={value} value={value}>
              {translate(`crm.operations.semaphore.${value}`)}
            </option>
          ))}
        </select>
        <SemaphoreBadge value={semaphore} />
      </div>
      <div className="space-y-1">
        <label className="text-sm font-medium" htmlFor="admin-reason">
          {translate("crm.operations.fields.semaphore_reason")}
        </label>
        <Textarea
          id="admin-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </div>
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <Button type="submit" className="h-11 sm:h-9" disabled={isPending}>
        {translate("crm.operations.actions.save")}
      </Button>
    </form>
  );
};
